import React, { useContext } from 'react';
import { Context } from '../../store/modalContext/store';
import { useAppSelector } from '../../store/store';
import {
	updateEmail,
	selectedEmailSelector,
	isTokenValidSelector,
	errorSelector,
	setShowRegister,
	setShowLogin,
	loginDataSelector,
	removeChangePasswordDetails,
	resetLoginData,
} from '../../store/login/loginSlice';
import { useDispatch } from 'react-redux';
import { loginTexts } from '../../business/loginHelper';
import {
	useLoginMutation,
	useSendLoginEmailMutation,
} from '../../store/api/api';
import {
	Alert,
	Box,
	Card,
	CardContent,
	Container,
	TextField,
	Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import { LoadingButton } from '@mui/lab';
import { APP_TYPES } from '@toriosoftware/torioshared/dist/config';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useHistoryNav } from '../../hooks/useHistoryNav';
import torioLogo from '../../assets/torio_logo.png';

const truncateURLAfterLogin = (url) => {
	const loginIndex = url.indexOf('/login');
	return loginIndex !== -1
		? url.substring(0, loginIndex + '/login'.length)
		: url;
};

const Login = ({ withToken }: any) => {
	//@ts-ignore
	const [modalState] = useContext(Context);
	const dispatch = useDispatch();
	let params = useParams();
	const [searchParams] = useSearchParams();
	let emailQuery = searchParams.get('email');
	let token = '';
	const location = useLocation();
	const history = useHistoryNav();

	const [isToken] = React.useState(false);
	const [firstName, setFirstName] = React.useState('');
	const [lastName, setLastName] = React.useState('');
	const [sendEmailLogin] = useSendLoginEmailMutation();
	const [login, loginStatus] = useLoginMutation();
	const [agreeToTerms] = React.useState(true);
	const [showSuccessBanner, setShowSuccessBanner] = React.useState(false);

	let email = useAppSelector(selectedEmailSelector);
	const forgotPasswordToken = '';
	const showForgotPassword = false;
	const isTokenValid = useAppSelector(isTokenValidSelector);
	const loginData = useAppSelector(loginDataSelector);
	let error = useAppSelector(errorSelector);

	let showRegister = false;
	let allowRegister = false;

	const changeUrl = React.useCallback(() => {
		const currentPath = location.pathname;
		const truncatedPath = truncateURLAfterLogin(currentPath);
		history(truncatedPath, { replace: true });
	}, [location.pathname, history]);

	const onEmailLogin = React.useCallback(
		async (tokenPara) => {
			try {
				const inputData = {
					email: emailQuery,
					password: tokenPara,
					phone: '',
					loginType: APP_TYPES.session.loginType.Email,
					sessionType: APP_TYPES.session.authType.Cookie,
					loginAsCompanyAdmin: 'yes',
					platform: APP_TYPES.platform.TorioWeb,
					countryPhoneCode: '',
					loginToken: token,
					isRegister: showRegister,
					firstName: firstName,
					lastName: lastName,
					agreeToTerms: agreeToTerms,
					modal: modalState,
					joinPlaceToken: '',
				};

				if (emailQuery && tokenPara) {
					let result = await login({
						...inputData,
						modal: modalState,
					}).unwrap();
					if (result) {
						changeUrl();
						if (result.success) {
							window.location.reload();
						}
					}
				}
			} catch (ex) {}
		},
		[
			emailQuery,
			token,
			showRegister,
			firstName,
			lastName,
			agreeToTerms,
			modalState,
			login,
			changeUrl,
		]
	);

	const onEmailChange = (value) => {
		dispatch(updateEmail(value));
	};

	const handleCloseSuccessBanner = () => {
		setShowSuccessBanner(false);
		dispatch(updateEmail(''));
	};

	const onSendEmail = React.useCallback(async () => {
		let res = await sendEmailLogin({
			email,
			modal: modalState,
		}).unwrap();
		if (res) {
			console.log('res', res);
			if (res.success) {
				setShowSuccessBanner(true);
			}
		}
	}, [email, modalState, sendEmailLogin]);

	React.useEffect(() => {
		(async () => {
			if (params && params.token) {
				onEmailLogin(params.token);
			}
		})();
	}, [params, onEmailLogin]);

	// Add keyboard event handler for Enter key
	React.useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Enter' && !loginStatus.isLoading) {
				event.preventDefault();
				onSendEmail();
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		// Cleanup event listener on component unmount
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [loginStatus.isLoading, onSendEmail]); // Dependencies to ensure we have latest values

	return (
		<Box
			sx={{
				minHeight: '100vh',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
				padding: 2,
			}}
		>
			<Container maxWidth="sm">
				<Card
					elevation={6}
					sx={{
						borderRadius: 3,
						overflow: 'visible',
					}}
				>
					<CardContent sx={{ p: { xs: 3, sm: 5 } }}>
						<Box sx={{ textAlign: 'center', mb: 4 }}>
							{!showSuccessBanner && (
								<>
									<Typography
										variant="h4"
										component="h1"
										fontWeight={700}
										gutterBottom
										sx={{ color: 'grey.900' }}
									>
										{loginTexts.headerText(
											forgotPasswordToken,
											showForgotPassword,
											showRegister
										)}
									</Typography>
									<Typography
										variant="body1"
										color="text.secondary"
										sx={{ mb: 2 }}
									>
										{loginTexts.lowerHeaderText(
											forgotPasswordToken,
											showForgotPassword,
											showRegister
										)}
									</Typography>
								</>
							)}
						</Box>

						{/* Success Banner */}
						{showSuccessBanner && (
							<Box sx={{ mb: 4 }}>
								<Alert
									severity="success"
									sx={{
										mb: 3,
										fontSize: '1rem',
										'& .MuiAlert-message': {
											width: '100%',
										},
									}}
								>
									<Typography variant="h6" fontWeight={600} gutterBottom>
										Check your email!
									</Typography>
									<Typography variant="body2">
										A login link was sent to your email. The code will expire in
										a few minutes. Do not share the link with nobody.
									</Typography>
								</Alert>
								<Button
									fullWidth
									variant="contained"
									size="large"
									onClick={handleCloseSuccessBanner}
									sx={{
										py: 1.5,
										fontSize: '1rem',
										fontWeight: 600,
										textTransform: 'none',
										borderRadius: 2,
										bgcolor: 'success.main',
										'&:hover': {
											bgcolor: 'success.dark',
										},
									}}
								>
									Ok
								</Button>
							</Box>
						)}

						{/* Form Content - Hidden when success banner is shown */}
						{!showSuccessBanner && (
							<>
								{/* Invitation Message */}
								{loginData.placeName && loginData.isTokenValid ? (
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											mb: 3,
											p: 2,
											bgcolor: 'grey.50',
											borderRadius: 2,
										}}
									>
										{loginData && loginData.image ? (
											<img
												src={loginData.image}
												alt="logo"
												style={{
													width: 80,
													height: 80,
													borderRadius: 12,
													marginBottom: 12,
												}}
											/>
										) : null}
										<Typography variant="body1" textAlign="center">
											You have been invited to join{' '}
											<strong>{loginData.placeName}</strong>.
										</Typography>
									</Box>
								) : null}

								{/* Register/Login Toggle */}
								{allowRegister ? (
									<Box sx={{ mb: 3, textAlign: 'center' }}>
										<ButtonGroup variant="outlined" fullWidth>
											<Button
												onClick={() => dispatch(setShowLogin())}
												variant={showRegister ? 'outlined' : 'contained'}
												disabled={loginStatus.isLoading}
											>
												Continue to
											</Button>
											<Button
												onClick={() => dispatch(setShowRegister())}
												variant={showRegister ? 'contained' : 'outlined'}
												disabled={loginStatus.isLoading}
											>
												Register
											</Button>
										</ButtonGroup>
									</Box>
								) : null}

								{/* Different Email Button */}
								{isToken ? (
									<Box sx={{ mb: 3, textAlign: 'center' }}>
										<Button
											onClick={() => dispatch(resetLoginData())}
											variant="outlined"
											disabled={loginStatus.isLoading}
										>
											Login with different email
										</Button>
									</Box>
								) : null}

								{/* Email Input */}
								{!loginStatus.isLoading && (
									<TextField
										fullWidth
										type="email"
										label="Email"
										value={email}
										onChange={(e) => onEmailChange(e.target.value)}
										disabled={forgotPasswordToken || isTokenValid}
										variant="outlined"
										sx={{ mb: 2 }}
										InputProps={{
											startAdornment: (
												<EmailOutlinedIcon
													sx={{ mr: 1, color: 'action.active' }}
												/>
											),
										}}
									/>
								)}

								{/* Register Fields */}
								{showRegister ? (
									<Box sx={{ mb: 2 }}>
										<TextField
											fullWidth
											label="First name"
											value={firstName}
											onChange={(e) => setFirstName(e.target.value)}
											variant="outlined"
											sx={{ mb: 2 }}
										/>
										<TextField
											fullWidth
											label="Last name"
											value={lastName}
											onChange={(e) => setLastName(e.target.value)}
											variant="outlined"
											sx={{ mb: 2 }}
										/>
									</Box>
								) : null}

								{/* Terms and Conditions */}
								{showRegister ? (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 3, textAlign: 'center' }}
									>
										By joining you agree to the{' '}
										<a
											href="https://portal.torio.is/home/terms"
											target="_blank"
											rel="noreferrer"
											style={{
												color: '#667eea',
												textDecoration: 'none',
												fontWeight: 600,
											}}
										>
											Terms and Conditions
										</a>{' '}
										and{' '}
										<a
											href="https://portal.torio.is/home/privacy"
											target="_blank"
											rel="noreferrer"
											style={{
												color: '#667eea',
												textDecoration: 'none',
												fontWeight: 600,
											}}
										>
											Privacy Policy
										</a>
										.
									</Typography>
								) : null}

								{/* Main Action Button */}
								<LoadingButton
									fullWidth
									variant="contained"
									size="large"
									loading={loginStatus.isLoading}
									onClick={async () => await onSendEmail()}
									sx={{
										py: 1.5,
										fontSize: '1rem',
										fontWeight: 600,
										textTransform: 'none',
										borderRadius: 2,
										mb: 2,
									}}
								>
									{loginTexts.buttonText(
										forgotPasswordToken,
										showForgotPassword,
										showRegister
									)}
								</LoadingButton>

								{/* Cancel Password Change */}
								{forgotPasswordToken ? (
									<Button
										fullWidth
										variant="outlined"
										color="error"
										size="large"
										onClick={() => {
											dispatch(removeChangePasswordDetails());
										}}
										href="/Login"
										sx={{
											py: 1.5,
											textTransform: 'none',
											borderRadius: 2,
											mb: 2,
										}}
									>
										Don't change password
									</Button>
								) : null}

								{/* Alerts */}
								<Box sx={{ mt: 3 }}>
									{error ? (
										<Alert severity="error" sx={{ mb: 2 }}>
											{error}
										</Alert>
									) : null}
									{!forgotPasswordToken ? (
										<Alert severity="info">
											This login page is for company users only. You need to be
											invited to login here.
										</Alert>
									) : null}
								</Box>
							</>
						)}
					</CardContent>
				</Card>
			</Container>
		</Box>
	);
};

export default Login;

// React.useEffect(() => {
// 	// dispatch(handleUrl());
// 	setTimeout(() => {
// 		if (token) {
// 			getJoinDetails({ token, modal: modalState });
// 			setIsToken(true);
// 		}
// 	}, 1000);
// }, [token]);

// React.useEffect(() => {
// 	// dispatch(handleUrl());
// 	if (!token && search) {
// 		const params = new URLSearchParams(search);
// 		const emailTemp = params.get('email');
// 		const resetPassTokenTemp = params.get('resetpassword');
// 		if (emailTemp && resetPassTokenTemp) {
// 			dispatch(setForgotPasswordToken(resetPassTokenTemp));
// 			dispatch(updateEmail(emailTemp));
// 		}
// 	}
// }, [search, token]);

// const handleVerifyJoinToken = async (token) => {
// 	if (token) {
// 		let result = await verifyJoinToken(token);
// 		console.log('result: ', result);
// 	}
// };

// React.useEffect(() => {
// 	handleVerifyJoinToken(token);
// }, [token]);
